import { FC, useMemo } from 'react';
import { Card } from 'shared/ui/card/card';
import { Icon } from 'shared/ui/icon/icon';
import cls from './score-card.module.scss';
import { useTranslation } from 'hooks';
import { IconTooltip } from 'shared/ui/icon-tooltip/icon-tooltip';
import { useAppSelector } from 'store/hooks';
import { CompetitorRow } from './competitor-row';
import { ICONS } from 'shared/constants/images';
import { ScoreCardExpiredProps } from './score-card.types';
import { VisibilityEntityBase } from 'store/api/apiTypes';
import { TrialBanner } from 'shared/ui/trial-banner/trial-banner';

export const ScoreCardExpired: FC<ScoreCardExpiredProps> = (
  props: ScoreCardExpiredProps
) => {
  const {
    competitors,
    selectCompetitor,
    selectedCompetitor,
    className,
    description,
    productNumber,
    title = 'visibility',
    size = 'large',
  } = props;

  const handleSelectCompetitor = (competitor?: VisibilityEntityBase) => {
    selectCompetitor?.(competitor);
  };

  const isLarge = size === 'large';
  const { t } = useTranslation();

  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );

  const projectAsCompetitor = useMemo<VisibilityEntityBase>(
    () => ({
      domain: selectedProject?.domain ?? '',
      overall_score: 0,
      description: '',
      name: '',
      mention_rate: 0,
      total_mentions: 0,
      mean_rank: 0,
      query_metrics: [],
    }),
    [selectedProject?.domain]
  );

  const cardCompetitors = useMemo(() => {
    const others = (competitors ?? []).filter(
      c => c.domain !== selectedProject?.domain
    );
    return [projectAsCompetitor, ...others];
  }, [competitors, projectAsCompetitor, selectedProject?.domain]);

  const maxScore = useMemo(
    () => Math.max(...cardCompetitors.map(c => c.mention_rate).concat(0)),
    [cardCompetitors]
  );

  return (
    <Card className={`${cls.scoreCard} ${className || ''}`}>
      <div className={cls.content}>
        <div className={cls.titleWrapper}>
          <h3 className={cls.title}>{t(title)}</h3>
          <IconTooltip id={`tooltip-${title}`}>
            <span>{t(`${title}Tooltip`)}</span>
          </IconTooltip>
        </div>
        <div className={`${cls.expired}`}>
          <div className={cls.lockIcon}>
            <Icon pathToIcon={ICONS.lockGray} size={16} />
          </div>
          <p>{t('scoreExpired')}</p>
        </div>
        {!isLarge && description && (
          <p className={cls.description}>{description}</p>
        )}
        {isLarge && (
          <>
            <div className={cls.competitorsHeader}>
              <h4 className={cls.competitorsTitle}>
                {t('competitorsComparison')}
              </h4>
              <IconTooltip id='competitors-tt'>
                <span>{t('competitorsTooltip')}</span>
              </IconTooltip>
            </div>
            <div className={cls.competitors}>
              {cardCompetitors
                ?.sort((a, b) => b.mention_rate - a.mention_rate)
                .map((competitor, index) => (
                  <CompetitorRow
                    isSelected={
                      selectedCompetitor?.domain === competitor.domain
                    }
                    key={competitor.domain}
                    competitor={competitor}
                    maxScore={maxScore}
                    currentDomain={selectedProject?.domain}
                    total={cardCompetitors.length}
                    onSelect={handleSelectCompetitor}
                    isTrialExpired
                    productNumber={productNumber}
                    competitorNumber={index + 1}
                    isTrial={true}
                  />
                ))}
            </div>
          </>
        )}
        {isLarge && (
          <TrialBanner
            className={cls.expiredBanner}
            title={t('scoreTrialExpiredBanner')}
            description={t('trialExpiredBannerDescription')}
          />
        )}
      </div>
    </Card>
  );
};
